import { useQuery } from "@tanstack/react-query";
import { client as insightsClient } from "@harmonyso/insights-engine-sdk";
import type { Cell } from "@/lib/cells/cells";
import { managementTenantId } from "@/lib/cells/management-tenant";

/**
 * Fetch a tenant's flow-map estate report as a self-contained HTML string.
 *
 * The report is HTML (not a typed JSON model), so we go through the configured
 * insights-engine axios instance directly (base URL + auth interceptor) with
 * responseType "text" rather than a generated SDK method. Rendered in a sandboxed
 * iframe by the caller. A 404 means no report has been generated for the tenant yet.
 *
 * The backoffice-api proxy requires a `tenant_id` query param — the management
 * (caller) tenant it injects as x-tenant-id for the downstream management gate,
 * the SAME value the recommendations hooks pass. The report's TARGET tenant is the
 * path segment; these are two different tenants. The management tenant is that of
 * `cell`, the cell the target tenant lives in, which also routes the call there.
 */
export function useAiDeflectionMap(
  tenantId: string | undefined,
  cell: Cell | undefined,
) {
  const managementTenant = cell ? managementTenantId(cell) : undefined;

  return useQuery<string>({
    queryKey: ["aiDeflectionMap", tenantId, cell?.id, managementTenant],
    enabled: Boolean(tenantId) && Boolean(cell),
    queryFn: async () => {
      const resp = await insightsClient.instance.get<string>(
        `/api/v1/internal/management/estate-report/${tenantId}`,
        {
          params: { tenant_id: managementTenant },
          responseType: "text",
        },
      );
      return resp.data;
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
  });
}
